import LegalPage from "@/components/LegalPage";

const sections = [
  {
    title: "Overview",
    paragraphs: [
      "BlackRide is a premium chauffeur and private transfer service serving Jeddah, Makkah, Madinah, and other major routes in Saudi Arabia. This Privacy Policy explains how we collect, use, store, and protect personal information when guests browse our website, contact us on WhatsApp, request a quote, or book a trip.",
      "We have adapted this policy for BlackRide's WhatsApp-first booking model, concierge support flow, and chauffeur operations process. By using the website or contacting BlackRide, you agree to the data practices described on this page.",
    ],
  },
  {
    title: "Information We Collect",
    paragraphs: [
      "We may collect information that guests provide directly when submitting a trip request, speaking with our team, or interacting with booking forms and WhatsApp messages.",
    ],
    bullets: [
      "Contact details such as name, phone number, WhatsApp number, and email address.",
      "Trip information such as pickup, destination, date, time, passengers, luggage, flight details, hotel details, and service preferences.",
      "Operational notes such as special assistance requests, concierge instructions, and route preferences.",
      "Technical information such as browser type, device type, pages visited, and website interaction analytics.",
    ],
  },
  {
    title: "How We Use Information",
    paragraphs: [
      "BlackRide uses personal information to respond to enquiries, arrange chauffeur services, confirm availability, prepare quotes, coordinate drivers, and support guests before, during, and after a trip.",
      "We may also use information to improve route planning, service quality, website performance, customer support, and safety procedures across our operations.",
    ],
    bullets: [
      "To respond to trip enquiries and booking requests.",
      "To send quotes, confirmations, payment instructions, and trip updates.",
      "To assign vehicles, chauffeurs, and operations staff to a booking.",
      "To provide live service communications such as pickup coordination or chauffeur tracking when available.",
      "To maintain records for finance, compliance, and customer service purposes.",
    ],
  },
  {
    title: "WhatsApp, Calls, and Communication Records",
    paragraphs: [
      "Because BlackRide currently operates with a WhatsApp-first booking flow, a significant part of guest communication may take place through WhatsApp, phone calls, and related support channels. Messages, call details, and guest instructions may be stored so that our operations team can review requests, confirm bookings, and support service delivery.",
      "Guests should only share information that is relevant to their trip. If a guest provides additional personal details, BlackRide may retain those details only to the extent reasonably necessary for the requested service or customer support.",
    ],
  },
  {
    title: "Sharing of Information",
    paragraphs: [
      "BlackRide does not sell guest personal data. We only share information where necessary to deliver the service or comply with legal or operational obligations.",
    ],
    bullets: [
      "With chauffeurs and internal operations staff who need trip details to deliver the booking.",
      "With payment processors or invoicing partners when handling deposits, final payments, or corporate billing.",
      "With technology providers that support website hosting, analytics, CRM, or operations systems.",
      "With authorities or legal advisors where disclosure is required by law, regulation, or a legitimate safety obligation.",
    ],
  },
  {
    title: "Cookies and Analytics",
    paragraphs: [
      "The website may use cookies, analytics tags, and similar technologies to understand page performance, topic interest, device usage, and conversion activity. This helps BlackRide improve content, website usability, and service communication.",
      "Guests can manage cookies through their browser settings. Disabling certain cookies may affect how some website features behave.",
    ],
  },
  {
    title: "Data Retention and Security",
    paragraphs: [
      "BlackRide takes reasonable technical and operational steps to protect personal information from unauthorized access, misuse, loss, or disclosure. Access to booking and guest information is restricted to team members and systems that need it for legitimate service delivery or management purposes.",
      "We retain personal information for as long as reasonably necessary to manage bookings, support customer service, meet legal obligations, handle payment records, and maintain internal business records.",
    ],
  },
  {
    title: "Your Rights",
    paragraphs: [
      "Depending on the laws that apply to you, you may have rights to request access to your personal information, request corrections, ask us to delete certain records, or object to certain uses of your information. BlackRide will review reasonable requests in line with applicable legal and operational requirements.",
      "To make a privacy-related request, contact us using the details at the bottom of this page.",
    ],
  },
  {
    title: "International Guests",
    paragraphs: [
      "BlackRide serves many international travelers. If you contact us from outside Saudi Arabia, your information may be processed by BlackRide personnel or service providers that support our Saudi operations. By submitting a request, you understand that your information may be processed for the purpose of arranging transport in Saudi Arabia.",
    ],
  },
  {
    title: "Policy Updates",
    paragraphs: [
      "BlackRide may update this Privacy Policy from time to time as the service, website, operations systems, or legal requirements evolve. The updated version will be posted on this page with a revised effective date.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      description="BlackRide Privacy Policy explaining how we collect, use, store, and protect guest information for chauffeur bookings, quotes, and transport services in Saudi Arabia."
      canonical="https://getblackride.com/privacy-policy"
      updatedLabel="Effective date: 14 May 2026"
      intro="This Privacy Policy explains how BlackRide handles guest information across the website, WhatsApp enquiries, chauffeur bookings, quotes, payments, and transport coordination in Saudi Arabia."
      sections={sections}
    />
  );
}
